import React from "react";
import { Link } from "react-router-dom";
import { Shield, Building2, Calendar, Zap, Users, ArrowRight, ChevronRight, Target } from "lucide-react";
import { motion } from "framer-motion";

const SECURITY_IMG = "/assets/manpower.png";
const FACILITY_IMG = "/assets/atf_facility.png";

const FadeUp = ({ children, delay = 0, className = "" }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.8, delay: delay / 1000, ease: [0.16, 1, 0.3, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const services = [
  {
    icon: Shield,
    title: "Security Services",
    subtitle: "Professional Guarding & Site Security",
    desc: "We deploy trained and verified security personnel to ensure controlled access, asset protection, and on-site safety across different environments.",
    features: ["Corporate & Retail Guarding", "Infrastructure Security", "Rapid Response Support", "Basic System Coordination"],
    color: "#835F21"
  },
  {
    icon: Calendar,
    title: "Event & Sports Security",
    subtitle: "Event Security Management",
    desc: "We provide security support for events of all sizes, ensuring crowd control, access management, and smooth coordination throughout the event.",
    features: ["Crowd Management", "Entry Control", "VIP Movement Support", "Emergency Handling"],
    color: "#141414"
  },
  {
    icon: Building2,
    title: "Facilities Management",
    subtitle: "Integrated Facility Support",
    desc: "We ensure smooth day-to-day facility operations through reliable manpower and structured processes to maintain clean and well-managed environments.",
    features: ["Housekeeping", "Technical Maintenance", "Front Desk Support", "General Facility Upkeep"],
    color: "#835F21"
  },
  {
    icon: Shield,
    title: "Industrial Security",
    subtitle: "Protection for Industrial & Manufacturing Sites",
    desc: "We provide security solutions designed for factories, warehouses, and industrial units, focusing on safety, control, and loss prevention.",
    features: ["Perimeter Security", "Internal Monitoring", "Safety Compliance Support", "Risk Control"],
    color: "#141414"
  },
  {
    icon: Users,
    title: "Executive Protection",
    subtitle: "Personal Security for Leadership",
    desc: "We offer professional protection services for business owners, executives, and VIPs, ensuring safety during travel, meetings, and public appearances.",
    features: ["Close Protection Officers", "Secure Movement Planning", "Risk Awareness"],
    color: "#835F21"
  },
  {
    icon: Target,
    title: "Consultancy & Risk Management",
    subtitle: "Practical Security Advisory",
    desc: "We help organizations identify risks and improve their existing security setup through on-ground assessments and practical recommendations.",
    features: ["Risk Assessment", "Compliance Review", "Security Planning", "Emergency Preparedness"],
    color: "#141414"
  },
];


const ServiceCard = ({ service, index }) => {
  return (
    <FadeUp delay={index * 100}>
      <div className="group relative bg-[#ffffff] rounded-3xl p-10 md:p-14 overflow-hidden transition-all duration-700 hover:shadow-[0_40px_100px_rgba(0,0,0,0.06)] border border-[#141414]/5 flex flex-col hover:-translate-y-2">
        {/* Decorative Number */}
        <span className="absolute top-8 right-10 font-display font-medium text-8xl text-[#141414]/[0.02] leading-none group-hover:text-[#835F21]/10 transition-colors duration-700 select-none">
          0{index + 1}
        </span>

        <div className="relative z-10 flex flex-col">
          <div className="w-20 h-20 rounded-2xl bg-[#835F21]/10 flex items-center justify-center mb-10 group-hover:bg-[#835F21] group-hover:scale-110 transition-all duration-500">
            <service.icon size={32} className="text-[#835F21] group-hover:text-white transition-colors duration-500" strokeWidth={1.5} />
          </div>

          <p className="font-sans text-[10px] font-bold tracking-[0.25em] text-[#835F21] uppercase mb-4">
            {service.subtitle}
          </p>
          <h2 className="font-display font-medium text-[#141414] text-3xl md:text-4xl mb-6 leading-tight tracking-tight">
            {service.title}
          </h2>
          <p className="font-sans text-[15px] text-[#141414]/60 leading-relaxed mb-10 font-light">
            {service.desc}
          </p>

          <div className="space-y-4 mb-12">
            {service.features.map((f, i) => (
              <div key={i} className="flex items-start gap-4">
                <Target size={14} className="text-[#835F21] mt-1 shrink-0" />
                <span className="font-sans text-[13px] font-medium text-[#141414]/80">{f}</span>
              </div>
            ))}
          </div>

          <div className="pt-8 border-t border-[#141414]/10 mt-auto">
            <Link to="/contact" className="inline-flex items-center gap-3 text-[#835F21] font-sans text-[11px] font-bold tracking-widest uppercase group/link hover:text-[#141414] transition-colors">
              Schedule Consultation 
              <div className="w-10 h-10 rounded-full border border-[#835F21]/30 flex items-center justify-center group-hover/link:border-[#141414] group-hover/link:bg-[#141414] group-hover/link:text-white transition-all">
                <ArrowRight size={16} />
              </div>
            </Link>
          </div>
        </div>
      </div>
    </FadeUp>
  );
};

export default function Services() {
  return (
    <main className="bg-[#F9F8F5] font-sans text-[#141414]" data-testid="services-page">
      {/* ── 01. Capabilities Hero ── */}
      <section className="bg-[#141414] h-[85vh] md:h-[75vh] flex items-center relative overflow-hidden" data-testid="services-hero">
        <div className="absolute inset-0 z-0">
          {/* Split-Screen Slanted Overlay */}
          <div 
            className="absolute inset-y-0 left-0 w-full lg:w-[40%] bg-[#F9F8F5]/90 backdrop-blur-sm z-10 pointer-events-none" 
            style={{ clipPath: "polygon(0 0, 85% 0, 100% 100%, 0 100%)" }}
          />
          
          <div className="absolute inset-0 z-0">
            <img src={SECURITY_IMG} alt="Service Portfolio" className="w-full h-full object-cover object-[85%_10%] opacity-60" />
          </div>
          <div className="absolute inset-0 bg-[#835F21]/5 mix-blend-multiply z-10 pointer-events-none" />
        </div>
        
        <div className="relative z-20 w-full lg:w-[40%] px-6 md:px-12 lg:pl-24 lg:pr-12 py-12 md:py-16">
            <div className="relative z-10">
              <span className="section-label block mb-6 text-[#835F21]">Security Capabilities</span>
              <h1 className="font-display font-medium text-[#141414] leading-[1.05] mb-8 tracking-tight"
                style={{ fontSize: "clamp(20px, 3vw, 44px)" }}
              >
                Reliable Security & <br />
                <span className="text-[#835F21] italic font-light mt-2 block">Manpower Services.</span>
              </h1>
              <p className="font-sans text-lg md:text-xl text-[#141414]/70 leading-relaxed font-light">
                Astraum Task Force provides trained personnel, structured security operations, and facility management services designed to protect people, assets, and day-to-day business operations.
              </p>
            </div>
          </div>
      </section>

      {/* ── 02. Staggered Service Architecture ── */}
      <section className="py-24 md:py-40 bg-[#F9F8F5] relative z-20" data-testid="services-list">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16">
            <div className="space-y-10 md:space-y-16">
              {services.filter((_, i) => i % 2 === 0).map((s, i) => (
                <ServiceCard key={`left-${i}`} service={s} index={i * 2} />
              ))}
            </div>
            <div className="space-y-10 md:space-y-16 md:pt-32">
              {services.filter((_, i) => i % 2 !== 0).map((s, i) => (
                <ServiceCard key={`right-${i}`} service={s} index={i * 2 + 1} />
              ))}
            </div>
          </div>
        </div>
      </section>


      {/* ── 04. Tactical Deployment CTA ── */}
      <section className="bg-[#cecabd] py-20 pb-32 md:pb-48" data-testid="services-cta">
        <div className="max-w-4xl mx-auto px-6">
          <FadeUp>
            <div className="bg-white rounded-[2rem] p-10 md:p-16 text-center shadow-[0_40px_80px_rgba(0,0,0,0.03)] relative overflow-hidden border border-[#141414]/5">
              {/* Subtle background element */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#835F21]/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
              
              <h2 className="font-display font-medium text-[#141414] leading-tight mb-6 tracking-tight" style={{ fontSize: "clamp(28px, 4vw, 42px)" }}>
                Secure Your <span className="text-[#835F21] italic font-light">Operations</span> with Confidence.
              </h2>
              <p className="font-sans text-base md:text-lg text-[#141414]/60 mb-10 max-w-xl mx-auto font-light">
                Partner with Astraum Task Force for reliable manpower, structured processes, and consistent security execution.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Link to="/contact" className="btn-primary bg-[#141414] text-white h-14 px-10 group">
                  Request Deployment
                  <ArrowRight size={18} className="ml-3 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link to="/how-we-work" className="btn-secondary border-[#141414]/20 text-[#141414] h-14 px-10 hover:bg-[#141414]/5 text-xs font-bold tracking-widest uppercase">
                  View Protocols
                </Link>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
